/**
 * Tradução dos erros do banco/PostgREST/GoTrue para mensagens de interface em pt-BR.
 * As RPCs do Vixio OS já levantam mensagens em português (ex.: "Critério de avanço: …");
 * elas passam como estão. Erros genéricos do Postgres e do PostgREST viram texto legível.
 */

export type ErrorKind =
  | "aal2"
  | "auth"
  | "forbidden"
  | "validation"
  | "conflict"
  | "not_found"
  | "unavailable"
  | "network"
  | "unknown";

export class VixioError extends Error {
  readonly kind: ErrorKind;
  readonly code: string | null;
  readonly details: string | null;
  constructor(message: string, kind: ErrorKind, code: string | null = null, details: string | null = null) {
    super(message);
    this.name = "VixioError";
    this.kind = kind;
    this.code = code;
    this.details = details;
  }
}

interface RawError {
  code?: unknown;
  message?: unknown;
  details?: unknown;
  hint?: unknown;
  status?: unknown;
  name?: unknown;
  error_description?: unknown;
  msg?: unknown;
}

const PT_HINT = /[ãõçáéíóúâêôà]|\b(não|sem|obrigatóri|inválid|critério|permissão|organização|proposta|parcela|valor|pessoa|papel)\b/i;

function looksPortuguese(msg: string): boolean {
  return PT_HINT.test(msg);
}

const AUTH_MESSAGES: Record<string, string> = {
  "Invalid login credentials": "E-mail ou senha incorretos.",
  "Email not confirmed": "E-mail ainda não confirmado. Veja sua caixa de entrada.",
  "User not found": "Usuário não encontrado.",
  "Signups not allowed for otp": "Não há conta com este e-mail. Peça um convite à administração.",
  "Signups not allowed for this instance": "Cadastro aberto desativado. Peça um convite à administração.",
  "New password should be different from the old password.": "A nova senha precisa ser diferente da atual.",
  "Password should be at least 6 characters.": "A senha precisa ter pelo menos 6 caracteres.",
  "Invalid TOTP code entered": "Código de verificação incorreto ou vencido.",
  "Invalid MFA Challenge ID": "Desafio de verificação expirou. Tente de novo.",
  "Email rate limit exceeded": "Muitos envios de e-mail seguidos. Aguarde um minuto.",
  "Token has expired or is invalid": "Link vencido ou já usado. Peça outro.",
  "JWT expired": "Sessão expirada. Entre novamente.",
};

export function translateError(err: unknown): VixioError {
  if (err instanceof VixioError) return err;
  if (err instanceof TypeError && /fetch|network|Failed/i.test(err.message)) {
    return new VixioError("Sem conexão com o servidor. Verifique a internet e tente de novo.", "network");
  }
  const e = (err ?? {}) as RawError;
  const code = typeof e.code === "string" ? e.code : typeof e.code === "number" ? String(e.code) : null;
  const rawMessage =
    typeof e.message === "string"
      ? e.message
      : typeof e.error_description === "string"
        ? e.error_description
        : typeof e.msg === "string"
          ? e.msg
          : err instanceof Error
            ? err.message
            : "Erro inesperado.";
  const details = typeof e.details === "string" ? e.details : null;
  // Mensagens do banco às vezes terminam com o nome técnico do campo: "… (pain_cost_estimate_cents)".
  const raw = rawMessage.replace(/\s*\([a-z_]+\)\s*$/, "");

  if (AUTH_MESSAGES[raw]) {
    const kind: ErrorKind = raw === "JWT expired" ? "auth" : "validation";
    return new VixioError(AUTH_MESSAGES[raw]!, kind, code, details);
  }
  if (/2FA|duas etapas|aal2/i.test(raw) || code === "insufficient_aal") {
    return new VixioError("Esta operação exige verificação em duas etapas (2FA).", "aal2", code, details);
  }
  if (code === "28000" || code === "PGRST301" || code === "PGRST302" || /JWT/i.test(raw)) {
    return new VixioError(looksPortuguese(raw) ? raw : "Sessão expirada. Entre novamente.", "auth", code, details);
  }
  if (code === "42501" || /permission denied/i.test(raw)) {
    return new VixioError(
      looksPortuguese(raw) ? raw : "Sem permissão para esta operação nesta organização.",
      "forbidden",
      code,
      details,
    );
  }
  if (code === "PGRST202" || code === "42883" || code === "PGRST205" || code === "42P01" || code === "3F000") {
    return new VixioError(
      "Função ou dado indisponível: o módulo não está instalado neste banco ou está em outra versão.",
      "unavailable",
      code,
      details,
    );
  }
  if (code === "P0002") return new VixioError(looksPortuguese(raw) ? raw : "Registro não encontrado.", "not_found", code, details);
  if (code === "23505") {
    return new VixioError(looksPortuguese(raw) ? raw : "Já existe um registro com esses dados.", "conflict", code, details);
  }
  if (code === "23514") {
    const constraint = /constraint "([^"]+)"/.exec(raw)?.[1];
    return new VixioError(
      `Valor fora das regras do cadastro${constraint ? ` (${constraint})` : ""}. Confira os campos.`,
      "validation",
      code,
      details,
    );
  }
  if (code === "23502") return new VixioError("Campo obrigatório não preenchido.", "validation", code, details);
  if (code === "22P02" || code === "22007" || code === "22008") {
    return new VixioError("Formato inválido em algum campo (número, data ou identificador).", "validation", code, details);
  }
  if (code === "22003") return new VixioError("Número fora do limite permitido.", "validation", code, details);
  if (code === "PGRST116") return new VixioError("Registro não encontrado.", "not_found", code, details);
  if (code && /^(P0001|22023|22000|23|22)/.test(code)) {
    return new VixioError(raw, "validation", code, details);
  }
  return new VixioError(looksPortuguese(raw) ? raw : `Erro inesperado: ${raw}`, "unknown", code, details);
}
